import type { WorkspaceInfo } from './types';

const SESSION_KEY = 'shw-powerpoint-session';

/**
 * Persisted authentication session. There is **no expiry timestamp** stored
 * with the access token — expiry is handled reactively: AuthContext attaches
 * an `onApiError` handler to the api-client which calls `logout()` on the
 * first HTTP 401. The user is then sent back to LoginView to re-authenticate
 * (which obtains a fresh token via the postMessage flow with the Showell app).
 *
 * If proactive expiry detection is ever wanted (decode the JWT `exp`, drop
 * the session at provider mount when expired), this is the type to extend.
 */
export type Session = {
  accessToken: string;
  userName: string | null;
  accountName: string | null;
  accountId: string | null;
  workspaceDisplayName: string | null;
  applications: WorkspaceInfo[] | null;
};

export function getStoredSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as Session;
  } catch {
    return null;
  }
}

export function storeSession(session: Session): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // localStorage may be unavailable in some contexts
  }
}

export function patchStoredSession(partial: Partial<Session>): void {
  const current = getStoredSession();
  if (!current) {
    return;
  }
  storeSession({ ...current, ...partial });
}

export function clearStoredSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}
